/**
 * Placeholder Articles for Dashboard
 * 
 * These are hardcoded articles used for the MVP dashboard redesign.
 * Articles are written from first-person perspective about leadership challenges.
 * References to books and insights use IDs from leadership_mentor_prompt.txt
 */

// Background images from InsightsFeedScreen
export const BACKGROUND_IMAGES = [
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1519681393784-d120267933ba?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1495195134817-aeb325a55b65?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
  'https://images.unsplash.com/photo-1475924156734-496f6cac6ec1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1080',
];

export interface BookReference {
  bookId: string;
  title: string;
  position: number; // Character position in content
}

export interface InsightReference {
  insightId: string;
  title: string;
  position: number; // Character position in content
}

export interface Article {
  id: string;
  title: string;
  imageUrl: string;
  content: string; // Markdown-style content with [Book:id] and [Insight:id] references
  bookReferences: BookReference[];
  insightReferences: InsightReference[];
  createdAt: number; // Unix timestamp
}

export const PLACEHOLDER_ARTICLES: Article[] = [
  {
    id: 'article-1',
    title: 'How I Learned to Stop Micromanaging and Trust My Team',
    imageUrl: BACKGROUND_IMAGES[0],
    content: `I used to think that being a good leader meant having all the answers. Every decision, every detail—I needed to be involved. My calendar was packed with meetings, my inbox overflowed, and my team seemed to wait for my approval on everything.

Then I hit a wall. I was burned out, my team was frustrated, and our velocity had slowed to a crawl. Something had to change.

The breakthrough came when I read [Book:31] by Andrew Grove. He introduced me to the concept of measuring true output, not just activity. I realized I was measuring my worth by how busy I was, not by what my team was actually achieving.

Grove's insight about [Insight:245] completely shifted my perspective. I started asking myself: "What is the actual output here?" Not "How many meetings did I attend?" but "Did we ship the feature? Did we solve the customer problem?"

I began delegating differently. Instead of telling my team exactly how to do things, I focused on the outcomes we needed. I learned about [Insight:248]—finding those high-leverage actions where a small amount of my time could have an outsized impact.

The transformation wasn't instant. My team needed time to adjust to their new autonomy. But within a few months, something remarkable happened: they started making better decisions than I would have made. They were closer to the problems, they understood the nuances, and they had the space to think creatively.

Now, I spend my time on what only I can do: setting direction, removing obstacles, and coaching. My team is more engaged, our output has doubled, and I actually have time to think strategically.

The lesson? Trust isn't just about believing in your team—it's about creating the systems and clarity that allow them to succeed without you.`,
    bookReferences: [
      { bookId: '31', title: 'High Output Management', position: 350 },
    ],
    insightReferences: [
      { insightId: '245', title: 'Measure and Maximize True Output Not Activity', position: 450 },
      { insightId: '248', title: 'Multiply Impact by Finding High-Leverage Actions', position: 750 },
    ],
    createdAt: Date.now() / 1000,
  },
  {
    id: 'article-2',
    title: 'The Feedback Conversation That Changed Everything',
    imageUrl: BACKGROUND_IMAGES[1],
    content: `I'll never forget the day Sarah, one of my best engineers, told me she was thinking about leaving. I was shocked. She was crushing it—shipping features, mentoring juniors, always positive in meetings. What was I missing?

"You never tell me how I'm actually doing," she said. "I get 'great job' in Slack, but I have no idea if I'm growing or just treading water."

That conversation haunted me. I thought I was being supportive by staying positive, but I was actually starving my team of the feedback they needed to grow.

I dove into [Book:161] and learned about Bill Campbell's approach to feedback. The key insight from [Insight:1319] hit me hard: you have to couple candor with care. It's not enough to be nice, and it's not enough to be honest—you need both, every single time.

I started having real conversations. Not annual reviews, but weekly check-ins where we talked about specific situations: "Here's what worked well in that client meeting. Here's where I think you could have pushed back more. What do you think?"

The framework from [Insight:250] helped me structure these conversations around actual performance improvement, not just vague encouragement. I learned to be specific, timely, and focused on behavior, not personality.

Sarah didn't leave. In fact, she told me six months later that these conversations were the most valuable part of her week. She knew exactly where she stood, what she was doing well, and where to focus her growth.

The irony? I thought I was being kind by avoiding tough feedback. But real kindness is helping people grow, even when it's uncomfortable.`,
    bookReferences: [
      { bookId: '161', title: 'Trillion Dollar Coach', position: 450 },
    ],
    insightReferences: [
      { insightId: '1319', title: 'Couple Candor With Care Every Time', position: 550 },
      { insightId: '250', title: 'Give Feedback that Improves Real Performance', position: 850 },
    ],
    createdAt: Date.now() / 1000 - 86400,
  },
  {
    id: 'article-3',
    title: 'Why I Stopped Chasing Every Opportunity',
    imageUrl: BACKGROUND_IMAGES[2],
    content: `Two years ago, I said yes to everything. New project? Yes. Speaking opportunity? Yes. Advisory role? Yes. I thought that's what successful leaders did—they seized every opportunity.

But I was drowning. My team couldn't get time with me. Strategic projects languished. I was everywhere and nowhere at once.

The wake-up call came during a quarterly review. We'd started ten initiatives and finished none. My CEO asked a simple question: "What are we actually trying to achieve this quarter?"

I didn't have a clear answer.

That's when I discovered [Book:233] and the concept of radical focus. The insight about [Insight:1906] was painful but necessary: cut your to-do list to a single ambitious goal. Not three goals. Not five. One.

I gathered my team and we did something radical: we chose one objective for the quarter. Just one. Everything else went into a "not now" list.

The pushback was immediate. "But what about the redesign?" "What about the new market?" I held firm, using the framework from [Insight:1909] to turn our vague aspirations into one measurable outcome.

The result? We actually finished something. Not only finished it, but did it better than anything we'd done before. The team had clarity. I had time to think. Stakeholders knew exactly what to expect.

Now, I'm ruthless about focus. Every opportunity gets measured against our one big goal. If it doesn't serve that goal, it's a "not now."

The paradox of leadership: saying no to good things is how you make room for great things.`,
    bookReferences: [
      { bookId: '233', title: 'Radical Focus', position: 450 },
    ],
    insightReferences: [
      { insightId: '1906', title: 'Cut Your To-Do List to a Single Ambitious Goal', position: 550 },
      { insightId: '1909', title: 'Turn Vague Goals into Measurable Outcomes', position: 850 },
    ],
    createdAt: Date.now() / 1000 - 172800,
  },
  {
    id: 'article-4',
    title: 'The Meeting That Taught Me About Real Leadership',
    imageUrl: BACKGROUND_IMAGES[3],
    content: `I used to run meetings like a democracy. Everyone got a vote. Everyone's opinion mattered equally. I thought that's what good leadership looked like—inclusive, collaborative, fair.

Then we spent three hours debating the color of a button.

I'm not exaggerating. Three hours. Twelve people. One button. We went in circles, revisited the same arguments, and left with no decision. I felt like a failure.

My mentor recommended [Book:156], and the chapter on [Insight:1274] changed how I think about leadership. The key insight: appoint one ultimate decision-maker for every project. Not a committee. One person.

It felt wrong at first. Wasn't I supposed to build consensus? But then I realized: consensus isn't the same as good decisions. Sometimes, the best thing a leader can do is make a call and move forward.

I started structuring meetings differently. Using the approach from [Insight:1315], I focused on surfacing the best ideas, not achieving consensus. We'd hear all perspectives, debate vigorously, but then one person—usually the person closest to the problem—would make the call.

The transformation was immediate. Meetings got shorter. Decisions got made. People felt heard but not stuck. And here's the surprising part: team satisfaction actually went up. Turns out, people don't want endless debate—they want clarity and forward motion.

I learned that real leadership isn't about making everyone happy. It's about creating the conditions for good decisions and then having the courage to make them.`,
    bookReferences: [
      { bookId: '156', title: 'Sprint', position: 350 },
    ],
    insightReferences: [
      { insightId: '1274', title: 'Appoint One Ultimate Decision-Maker Every Project', position: 450 },
      { insightId: '1315', title: 'Run Meetings for Best Ideas, Not Consensus', position: 750 },
    ],
    createdAt: Date.now() / 1000 - 259200,
  },
  {
    id: 'article-5',
    title: 'What I Wish I Knew About Building Trust',
    imageUrl: BACKGROUND_IMAGES[4],
    content: `My first year as a manager was rough. I had the title, the authority, the seat at the table. But something was off. My team was polite but distant. They did what I asked, but nothing more. There was no spark, no real collaboration.

I thought maybe I needed to be more assertive. Or maybe more friendly. I tried both. Neither worked.

Then I had coffee with a senior leader who asked me a simple question: "Do your people trust you?"

I didn't know how to answer. I thought trust was something that just happened over time. I was wrong.

Reading [Book:70] opened my eyes to what I was missing. The insight about [Insight:571] hit me hard: you can't build high performance on a foundation of fear and blame. You need courage.

I started being more vulnerable. When I made a mistake, I admitted it publicly. When I didn't know something, I said so. When someone took a risk that didn't work out, I focused on what we learned, not who to blame.

The framework from [Insight:1317] became my guide: build an envelope of trust first, before anything else. That means showing up consistently, being honest even when it's hard, and demonstrating that you care about people as humans, not just as resources.

It took months, but slowly things changed. People started bringing me problems earlier. They shared ideas that weren't fully formed. They took bigger risks. The team's output didn't just improve—it transformed.

Trust isn't a nice-to-have. It's the foundation of everything else. And it starts with you being willing to be human.`,
    bookReferences: [
      { bookId: '70', title: 'Daring Greatly', position: 450 },
    ],
    insightReferences: [
      { insightId: '571', title: 'Grow a Culture of Courage—Not Fear and Blame', position: 550 },
      { insightId: '1317', title: 'Build an Envelope of Trust First', position: 850 },
    ],
    createdAt: Date.now() / 1000 - 345600,
  },
];
